import { UNO_MOBILE_OPERATOR_IDS } from '../api/unomobile'
import type { DocumentReadKind } from '../api/documents'
import { PROFILE_1MOBILE, PROFILE_A2A, PROFILE_ACEA, PROFILE_ENEGA, PROFILE_ENEL, PROFILE_FASTWEB, portalProfileFor, type PortalProfile } from './portalProfiles'

/**
 * Regole dei portali per operatore: la pratica chiede solo i campi del portale su cui verra' caricata. Qui si
 * decide che tipo di pratica e' (energia, telefonia fissa, SIM mobile), quali pagamenti accetta, se c'e' una
 * bolletta da spedire, quali documenti servono e quali opzioni richiedono un modulo firmato.
 * I campi veri e propri restano in `PraticaPortalSections`.
 */
export type PortalKind = 'energia' | 'telco' | 'mobile'

export interface PortalModule {
  /** Casella della pratica che attiva l'opzione (valore 'SI'). */
  flag: string
  label: string
  /** Mostrata solo se la pratica ha questa fornitura. */
  supply?: 'luce' | 'gas'
  /** Link al modulo vuoto sul portale dell'operatore (serve essere entrati nel portale). */
  url?: string
}

export interface OperatorPortalConfig {
  kind: PortalKind
  /** Vuoto = il portale non chiede la modalita' di pagamento (es. SIM ricaricabile). */
  payments: string[]
  modules: PortalModule[]
  /** Il portale chiede come spedire la bolletta (email o carta). */
  billDelivery: boolean
  /** Documenti da caricare e leggere all'inizio della pratica. */
  documents: DocumentReadKind[]
  /** Campi del modulo del portale (sezioni, obbligatori). Senza profilo: sezioni generiche energia/telefonia. */
  profile?: PortalProfile
  /** Tecnologie della linea fissa (campo TECNOLOGIA): il caricamento sul portale ne ricava la copertura. */
  technologies?: string[]
  /** Riquadro per la PDA firmata cartacea (allegato di tipo `pda_firmata`), facoltativa: senza, su Teamwork vale PDA digitale. */
  signedPda?: boolean
  /** SIM aggiuntive (bundle F+M o fisso con SIM): ognuna diventa una pratica collegata. */
  multiSim?: boolean
  /**
   * Ermes (ContrattiSmart): la bozza la crea il CRM. Chiede residente/non residente (tariffa e listino), le operazioni
   * sul sito della pagina Allegati (voltura, subentro...), il porta un amico per le aziende e la bolletta come fattura.
   */
  ermes?: boolean
  /** Valori proposti per i portali senza profilo (per quelli con profilo vale `profile.defaults`). */
  defaults?: Record<string, string>
}

/** Allegato della PDA firmata: tipo documento che il caricamento su Teamwork allega come contratto firmato. */
export const SIGNED_PDA_DOCUMENT_TYPE = 'pda_firmata'
export const SIGNED_PDA_KEY = 'PDA_FIRMATA'

export const PAYMENT_LABELS: Record<string, string> = {
  SDD: 'Addebito su conto (SDD)',
  BONIFICO: 'Bonifico',
  BOLLETTINO: 'Bollettino',
  CARTA: 'Carta di credito',
}

const ERMES_DOC = (key: string) => `https://ebox.ermespower.it/download.php?mod=Documenti&op=generale&filter[key]=${key}`

const ENERGIA_MODULES: PortalModule[] = [
  { flag: 'IVA_10_LUCE', label: 'IVA al 10% luce', supply: 'luce' },
  { flag: 'IVA_10_GAS', label: 'IVA al 10% gas', supply: 'gas' },
  { flag: 'FLG_SCONTO_ACCISE_LUCE', label: 'Agevolazione accise luce', supply: 'luce' },
  { flag: 'FLG_SCONTO_ACCISE_GAS', label: 'Agevolazione accise gas', supply: 'gas' },
]

const ERMES_MODULES: PortalModule[] = [
  { flag: 'IVA_10_LUCE', label: 'IVA al 10% luce', supply: 'luce', url: ERMES_DOC('0d3178950a28fd36040938b8d2c61b6e') },
  { flag: 'IVA_10_LUCE_CONDOMINIO', label: 'IVA al 10% luce condominio', supply: 'luce', url: ERMES_DOC('f715cd7bc17e42dafabfea4e96096d0c') },
  { flag: 'IVA_10_GAS', label: 'IVA al 10% gas', supply: 'gas', url: ERMES_DOC('ed256b8a4e4aa0d568fa67affe3becce') },
  { flag: 'FLG_SCONTO_ACCISE_LUCE', label: 'Agevolazione accise luce', supply: 'luce', url: ERMES_DOC('1aea64d24b7a9221044c101902ce61a3') },
  { flag: 'FLG_SCONTO_ACCISE_GAS', label: 'Accisa agevolata gas', supply: 'gas', url: ERMES_DOC('92d7f225176113bcd2fbd253eb57c2b1') },
]

const ALL_DOCS: DocumentReadKind[] = ['carta_identita', 'bolletta', 'visura']
const ENERGIA: OperatorPortalConfig = { kind: 'energia', payments: ['SDD', 'BONIFICO'], modules: ENERGIA_MODULES, billDelivery: true, documents: ALL_DOCS }
const TELCO: OperatorPortalConfig = { kind: 'telco', payments: ['SDD', 'CARTA'], modules: [], billDelivery: true, documents: ALL_DOCS }
/**
 * 1Mobile (portale Effortel CIM): SIM ricaricabile. Il portale chiede anagrafica con documento, SIM (ICCID) ed
 * eventuale portabilita': niente pagamento, niente bolletta, niente forniture (deploy/1mobile/campi-processi.md).
 */
const UNO_MOBILE: OperatorPortalConfig = { kind: 'mobile', payments: [], modules: [], billDelivery: false, documents: ['carta_identita', 'visura'], profile: PROFILE_1MOBILE }

const ENERGIA_PROFILES: PortalProfile[] = [PROFILE_ACEA, PROFILE_A2A, PROFILE_ENEGA, PROFILE_ENEL]
const TELCO_NAMES = ['FASTWEB', 'TIM', 'WIND', 'SKY', 'EOLO', 'RETELIT', 'VODAFONE', 'ILIAD']
const UNO_MOBILE_NAMES = ['UNOMOBILE', '1MOBILE']

/** Fastweb si carica su Teamwork: serve la tecnologia della linea (coperture del portale); la PDA cartacea è facoltativa. */
const FASTWEB: OperatorPortalConfig = {
  ...TELCO,
  billDelivery: false,
  profile: PROFILE_FASTWEB,
  technologies: ['GPON', 'VULA', 'SEMI_VULA', 'FTTN', 'FTTH', 'BS NGA', 'BS GPON', 'FWA', 'ADSL ULL', 'ADSL WHS', 'SHDSL'],
  signedPda: true,
  multiSim: true,
}

/** Regole per l'operatore scelto, riconosciuto dall'id o dal nome. Senza operatore: energia generica. */
export function operatorPortalConfig(operatorName: string | undefined, operatorId?: number | null): OperatorPortalConfig {
  const n = (operatorName ?? '').toUpperCase()
  if ((operatorId != null && UNO_MOBILE_OPERATOR_IDS.includes(operatorId))
    || UNO_MOBILE_NAMES.some(t => n.replace(/[\s_-]/g, '').includes(t))) return UNO_MOBILE
  if (n.includes('FASTWEB')) return FASTWEB
  // Ermes: nella pagina "Altro" del portale si sceglie sempre la prima voce (esecuzione anche durante il ripensamento).
  if (n.includes('ERMES')) return { ...ENERGIA, payments: ['SDD', 'BONIFICO', 'BOLLETTINO'], modules: ERMES_MODULES, ermes: true, defaults: { AUTORIZZAZIONE_RIPENSAMENTO: 'SI' } }
  // Portali con un modulo censito (Teamwork, Coraly): solo i campi di quel modulo.
  const profile = portalProfileFor(n)
  if (profile) {
    const energia = ENERGIA_PROFILES.includes(profile)
    return { kind: energia ? 'energia' : 'telco', payments: [], modules: [], billDelivery: false, documents: ['carta_identita', 'visura'], profile }
  }
  if (TELCO_NAMES.some(t => n.includes(t))) return TELCO
  return ENERGIA
}

/** Tipo cliente del portale ricavato dalla scelta unica Privato/Azienda della pratica. */
export function tipoClienteFromSegment(segment: string): string {
  return segment === 'business' ? 'AZIENDA' : segment === 'consumer' ? 'PRIVATO' : ''
}
